/**
 * Organizations list CSV export (phase 1: name, promo code, type).
 *
 * The export contains exactly the rows the Organizations list shows: the same search and Status /
 * Prospect / Type / Kind filters (shared buildOrganizationListFilter), the same `is_archived = false`
 * scope and the same sort, across ALL pages. PostgREST silently caps a response at 1,000 rows, so the
 * export reads in pages of at most 1,000 until a short page comes back.
 */

import { supabase } from "./supabase";
import {
  buildOrganizationListFilter,
  type OrganizationListFilters,
} from "./organizations.service";
import { organizationTypeLabel } from "../types/organization.types";
import { rowsToCsv, type ExportField } from "../utils/segmentCsv";

/** Rows per request. Must stay at or below PostgREST's 1,000-row cap, or pages would be cut short. */
export const ORGANIZATION_EXPORT_PAGE_SIZE = 1000;
/** Hard stop so a misbehaving page source can never loop forever (1,000 pages = 1,000,000 rows). */
export const ORGANIZATION_EXPORT_MAX_PAGES = 1000;

export interface OrganizationExportRow {
  id: string;
  name: string | null;
  promo_code: string | null;
  organization_type: string | null;
}

export const ORGANIZATION_EXPORT_COLUMNS: ExportField[] = [
  { key: "name", label: "Organization Name" },
  { key: "promo_code", label: "Promo Code" },
  { key: "organization_type", label: "Organization Type" },
];

/**
 * Read every page from `fetchPage(from, to)` (inclusive range, like PostgREST's .range) until a page
 * returns fewer than `pageSize` rows. Any page error is thrown, never swallowed.
 */
export async function fetchAllPages<T>(
  fetchPage: (from: number, to: number) => Promise<T[]>,
  pageSize: number = ORGANIZATION_EXPORT_PAGE_SIZE,
  maxPages: number = ORGANIZATION_EXPORT_MAX_PAGES,
): Promise<T[]> {
  const all: T[] = [];
  for (let page = 0; page < maxPages; page++) {
    const from = page * pageSize;
    const rows = await fetchPage(from, from + pageSize - 1);
    all.push(...rows);
    if (rows.length < pageSize) {
      return all;
    }
  }
  throw new Error(
    `Export stopped after ${maxPages} pages of ${pageSize} rows; narrow the filters and try again.`,
  );
}

/** CSV text: header row then one row per organization, type shown with its display label. */
export function buildOrganizationsCsv(rows: OrganizationExportRow[]): string {
  return rowsToCsv(
    rows.map((r) => ({
      name: r.name ?? "",
      promo_code: r.promo_code ?? "",
      organization_type: organizationTypeLabel(r.organization_type),
    })),
    ORGANIZATION_EXPORT_COLUMNS,
  );
}

export interface OrganizationExportParams extends OrganizationListFilters {
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/** Fetch every organization the list currently shows (all pages), with only the exported columns. */
export async function fetchOrganizationsForExport(
  params: OrganizationExportParams,
): Promise<OrganizationExportRow[]> {
  const { sortBy = "created_at", sortOrder = "desc", ...filters } = params;
  const applyFilters = await buildOrganizationListFilter(filters);
  return fetchAllPages<OrganizationExportRow>(async (from, to) => {
    const base = supabase
      .from("organizations")
      .select("id, name, promo_code, organization_type")
      .eq("is_archived", false);
    // Explicit type argument: letting TS infer Q from the PostgREST builder recurses too deeply (TS2589).
    const query = applyFilters<typeof base>(base);
    // Same sort as the list, plus id as a tiebreaker so rows never shift between pages.
    const { data, error } = await query
      .order(sortBy, { ascending: sortOrder === "asc" })
      .order("id", { ascending: true })
      .range(from, to);
    if (error) {
      throw error;
    }
    return (data ?? []) as OrganizationExportRow[];
  });
}

/** File name for today's export, e.g. organizations-2026-10-06.csv (local date). */
export function organizationsExportFilename(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `organizations-${y}-${m}-${d}.csv`;
}
